import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchChatStatus, type ChatType } from '../services/chat';
import { CHAT_STATUS_KEY } from './queryKeys';

export interface ChatStatusResult {
    /** A patient with no chat_status row for this thread reads as "nothing unread". */
    isUnread: boolean;
    otherSideLastSeenAt: string | null;
    team: string | null;
    teamRoleId: string | null;
    assignedTo: string | null;
    reviewedBy: string | null;
    isLoading: boolean;
    refresh: () => Promise<void>;
}

export function useChatStatus(patientId: string, chatType: ChatType): ChatStatusResult {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: CHAT_STATUS_KEY(patientId, chatType),
        queryFn: () => fetchChatStatus(patientId, chatType),
        enabled: !!patientId,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: CHAT_STATUS_KEY(patientId, chatType) });
    }, [queryClient, patientId, chatType]);

    return {
        isUnread: data?.hasUpdate ?? false,
        otherSideLastSeenAt: data?.otherSideLastSeenAt ?? null,
        team: data?.team ?? null,
        teamRoleId: data?.teamRoleId ?? null,
        assignedTo: data?.assignedTo ?? null,
        reviewedBy: data?.reviewedBy ?? null,
        isLoading,
        refresh
    };
}
